import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ContactGroup } from '@app/core/interfaces/c5/contact-group';
import { PhoneBookContact } from '@app/core/interfaces/c5/phone-book-contact';
import { SecurityPermissionName } from '@app/core/models/permission';
import { DatatablePage } from '@app/shared/datatable-page';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { ContactNamePipe } from '@app/shared/pipes/contact-name.pipe';
import { isEmpty } from '@app/utils/string';
import { ReplaySubject } from 'rxjs';
import { UpdateContactComponent } from '../update-contact/update-contact.component';
import { ContactViewModel } from './contact-view-model';
import { ContactsMode } from './contacts.mode';

@Component({
  selector: 'portal-contacts',
  templateUrl: './contacts.component.html',
  styles: [],
  standalone: false,
})
export class ContactsComponent extends DatatablePage<ContactViewModel> implements OnChanges {
  @Input()
  mode!: ContactsMode;

  @Input()
  contacts: PhoneBookContact[];

  @Input()
  contactGroups: ContactGroup[];

  @Input()
  get loading(): boolean {
    return super.loading;
  }

  set loading(value: boolean) {
    super.loading = value;
  }

  @Input()
  reload: () => void;

  @Input()
  remove: (contact: Partial<ContactViewModel>) => Promise<any>;

  @Input()
  update: (contact: ContactViewModel | Partial<ContactViewModel>) => Promise<any>;

  Permission = SecurityPermissionName;

  contactsMode = ContactsMode;

  private contacts$ = new ReplaySubject<ContactViewModel[]>(1);

  constructor(
    private dialogService: DialogService,
    private contactNamePipe: ContactNamePipe
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('contacts' in changes && changes.contacts.currentValue != null) {
      const contacts = changes.contacts.currentValue as PhoneBookContact[];

      this.contacts$.next(contacts.map((contact) => this.mapContact(contact)));
    }
  }

  ngOnInit() {
    super.ngOnInit();

    this.noInitialOrder();
  }

  canModify(contact: ContactViewModel): Boolean {
    if (
      (this.mode === ContactsMode.CALL_CENTER_ADMIN || this.mode === ContactsMode.CALL_CENTER_SUPERVISOR) &&
      contact.bookType === 'CALL_CENTER'
    ) {
      return true;
    } else {
      return false;
    }
  }

  add() {
    this.dialogService.open(UpdateContactComponent).withInput({
      mode: this.mode,
      contactGroups: this.contactGroups,
      update: this.update,
    });
  }

  delete(contact: ContactViewModel) {
    this.dialogService.openRemovalConfirmationDialog(() => {
      return this.remove(contact._original);
    });
  }

  edit(contact: ContactViewModel) {
    this.dialogService.open(UpdateContactComponent).withInput({
      mode: this.mode,
      contact: contact._original,
      contactGroups: this.contactGroups,
      update: this.update,
    });
  }

  fetchData() {
    return this.contacts$;
  }

  getColumns() {
    if (this.mode === ContactsMode.CALL_CENTER_AGENT) {
      return this.outletColumns(['name', 'businessPhone', 'email']);
    }

    return [
      ...this.outletColumns(['name', 'businessPhone', 'email']),
      this.outletColumn('buttons').notSortable(),
    ];
  }

  protected customFilter = (data: ContactViewModel, dataIndex: number, term: string) => {
    return data.companyInfo?.toLowerCase().indexOf(term) > -1;
  };

  refresh() {
    this.reload();
  }

  private mapContact(entry: PhoneBookContact): ContactViewModel {
    return {
      _original: entry,
      bookType: entry.bookType,
      businessPhone: entry.businessPhone,
      businessPhone2: entry.businessPhone2,
      businessFax: entry.businessFax,
      companyInfo: this.companyInfo(entry),
      email: entry.email,
      email2: entry.email2,
      extension: entry.extension,
      homePhone: entry.homePhone,
      homePhone2: entry.homePhone2,
      homeFax: entry.homeFax,
      id: entry.id,
      mobilePhone: entry.mobilePhone,
      mobilePhone2: entry.mobilePhone2,
      name: this.contactNamePipe.transform(entry),
      groups: entry.groups,
      groupsInfo: this.groupsInfo(entry),
      vipPriority: entry.vipPriority,
    };
  }

  private companyInfo(entry: PhoneBookContact): string {
    const company = isEmpty(entry.firstName) && isEmpty(entry.lastName) ? null : entry.company;
    return [company, entry.department, entry.jobTitle].filter((s) => !!s === true && s !== '').join(' | ');
  }

  private groupsInfo(entry: PhoneBookContact): string {
    return entry.groups
      .map((g) => {
        if (g.name === '') {
          return '<i class="far fa-star"></i>';
        } else {
          return g.name;
        }
      })
      .join(' | ');
  }
}
